import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './MicrositeBlog.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeTable from '../components/MicrositeTable/MicrositeTable'
import MicrositePagination from '../components/MicrositePagination/MicrositePagination'
import MicrositeSearchBar from '../components/MicrositeSearchBar/MicrositeSearchBar'
import MicrositeStatusFilter from '../components/MicrositeStatusFilter/MicrositeStatusFilter'
import MicrositeStatusBadge from '../components/MicrositeStatusBadge/MicrositeStatusBadge'
import MicrositeViewCountBadge from '../components/MicrositeViewCountBadge/MicrositeViewCountBadge'
import MicrositeDeleteConfirm from '../components/MicrositeDeleteConfirm/MicrositeDeleteConfirm'

const PAGE_LIMIT = 20

const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'scheduled', label: 'Scheduled' }
]

const MicrositeBlog = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [toast, setToast] = useState(null)
    const [rows, setRows] = useState([])
    const [statusFilter, setStatusFilter] = useState('')
    const [search, setSearch] = useState('')
    const [cursorStack, setCursorStack] = useState([0])
    const [nextCursor, setNextCursor] = useState(null)

    const currentCursor = cursorStack[cursorStack.length - 1]

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: activeStore?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadPosts = useCallback(async (cursor, status) => {
        setLoading(true)
        const res = await post('micrositeGetBlogPosts', { limit: PAGE_LIMIT, cursor: cursor || 0, status: status || '' })
        if (res?.status === 'success') {
            setRows(res.response?.data ?? [])
            setNextCursor(res.response?.nextCursor ?? null)
        } else {
            showToast('Could not load blog posts. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadPosts(currentCursor, statusFilter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, currentCursor, statusFilter])

    const handleStatusChange = (status) => {
        setStatusFilter(status)
        setCursorStack([0])
    }

    const filteredRows = useMemo(() => {
        if (!search.trim()) return rows
        const q = search.trim().toLowerCase()
        return rows.filter((r) => r.title?.toLowerCase().includes(q) || r.slug?.toLowerCase().includes(q))
    }, [rows, search])

    const handleDelete = async (ID) => {
        const res = await post('micrositeDeleteBlogPost', { ID })
        if (res?.status === 'success') {
            setRows((prev) => prev.filter((r) => r.ID !== ID))
            showToast('Post deleted')
        } else {
            showToast('Could not delete post', 'error')
        }
    }

    const formatDate = (d) => {
        if (!d) return '—'
        try {
            return new Date(d.replace(' ', 'T')).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
        } catch {
            return d
        }
    }

    const columns = [
        {
            key: 'title',
            label: 'Post',
            render: (row) => (
                <div className={styles.postCell}>
                    {row.coverImage && (
                        <img
                            src={row.coverImage}
                            alt=""
                            className={styles.cover}
                            onError={(e) => { e.currentTarget.style.display = 'none' }}
                        />
                    )}
                    <div className={styles.postText}>
                        <span className={styles.title}>{row.title}</span>
                        <span className={styles.slug}>{row.slug}</span>
                    </div>
                </div>
            )
        },
        {
            key: 'status',
            label: 'Status',
            render: (row) => <MicrositeStatusBadge status={row.status} />
        },
        {
            key: 'viewCount',
            label: 'Views',
            render: (row) => <MicrositeViewCountBadge count={row.viewCount ?? 0} />
        },
        {
            key: 'publishedAt',
            label: 'Published',
            render: (row) => <span className={styles.dateText}>{formatDate(row.publishedAt)}</span>
        },
        {
            key: 'actions',
            label: '',
            align: 'right',
            render: (row) => (
                <div className={styles.rowActions}>
                    <button
                        type="button"
                        className={styles.editLink}
                        onClick={() => navigate(`/microsite/blog/${row.ID}/edit`)}
                    >
                        Edit
                    </button>
                    <MicrositeDeleteConfirm mode="inline" onConfirm={() => handleDelete(row.ID)} />
                </div>
            )
        }
    ]

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Blog"
                subtitle="Share news, tips and updates with your customers."
                primaryAction={{ label: 'New post', icon: <Plus size={14} />, onClick: () => navigate('/microsite/blog/new') }}
            />

            <div className={styles.toolbar}>
                <MicrositeStatusFilter value={statusFilter} onChange={handleStatusChange} options={STATUS_FILTER_OPTIONS} />
                <MicrositeSearchBar placeholder="Search by title or slug…" onSearch={setSearch} />
            </div>

            <MicrositeTable
                columns={columns}
                rows={filteredRows}
                getRowKey={(row) => row.ID}
                loading={loading}
                emptyMessage="No blog posts yet"
                emptyDescription="Write your first post to share news and updates"
            />

            <MicrositePagination
                hasPrev={cursorStack.length > 1}
                hasNext={!!nextCursor}
                onPrev={() => setCursorStack((s) => s.slice(0, -1))}
                onNext={() => setCursorStack((s) => [...s, nextCursor])}
                loading={loading}
                currentCount={filteredRows.length}
            />
        </div>
    )
}

export default MicrositeBlog